import { CheckIcon } from '@heroicons/react/20/solid'
import { Document, InfoCircleFilled, Spreadsheet } from '@/assets/icons'
import type { SVGProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { useState } from 'react'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { Tooltip } from '@/components/ui/tooltip/Tooltip'
import { ExportFormat } from './schemas'
import { wordTooLarge, WORD_TOO_LARGE } from './limits'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useDispatch, useSelector } from 'react-redux'
import { IconButton } from '@/components/icon-button/IconButton'
import TableDownload from '@/assets/icons/table-download.svg?react'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { MenuItemContent } from '@/components/menu/MenuItemContent'
import { getMenuItemClassName } from '@/components/menu/menu-item-styles'
import { RootState } from '@/store/store'
import { TableQuery } from '../schemas'
import { ExportActions } from './store/slice'

const FOOTNOTE_HELP = 'Keeps footnote numbers in each cell and includes links to source documents.'

const ExcelIcon = (props: SVGProps<SVGSVGElement>) => <Spreadsheet {...props} className={twMerge(props.className, 'text-brand-neutral-700')} />
const WordIcon = (props: SVGProps<SVGSVGElement>) => <Document {...props} className={twMerge(props.className, 'text-brand-neutral-700')} />

/** Remains in menu navigation so unavailable Word choices can explain their limit. */
function UnavailableWordItem({
  label,
  icon,
  open,
  onOpenChange,
}: {
  label: string
  icon?: typeof WordIcon
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <CustomizableTooltip
      title={WORD_TOO_LARGE}
      describeChild
      tooltipClassName="flex items-center !max-w-[calc(100vw-32px)] !bg-surface-inverse !text-text-on-inverse-primary text-2xs font-medium"
      open={open}
      onOpen={() => onOpenChange(true)}
      onClose={() => onOpenChange(false)}
      placement="left"
    >
      <DropdownMenu.Item
        aria-disabled="true"
        data-disabled=""
        className={twMerge(
          getMenuItemClassName({ disabled: true, variant: 'neutral' }),
          'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 [&_span]:!text-true-gray-300 [&_svg]:!text-true-gray-300'
        )}
        onFocus={() => onOpenChange(true)}
        onSelect={(event) => {
          event.preventDefault()
          onOpenChange(true)
        }}
      >
        <MenuItemContent label={label} icon={icon} disabled variant="neutral" />
      </DropdownMenu.Item>
    </CustomizableTooltip>
  )
}

/** Generate filtered or complete downloads while preserving the current ordering. */
export default function DownloadTable({
  tableId,
  query,
  rowCount,
  totalRowCount,
  columnIds,
  pinnedColumnIds,
  documentBased = false,
  disabled = false,
}: {
  tableId: string
  rowCount?: number | null
  totalRowCount?: number | null
  query: TableQuery
  columnIds?: string[]
  pinnedColumnIds?: string[]
  documentBased?: boolean
  disabled?: boolean
}) {
  const [format, setFormat] = useState<ExportFormat | null>(null)
  const [includeFootnotes, setIncludeFootnotes] = useState(false)
  const [wordLimitOpen, setWordLimitOpen] = useState(false)
  const dispatch = useDispatch()
  const state = useSelector((root: RootState) => root.tableExports?.byTable[tableId])
  const hasFilters = query.filters.length > 0 || Boolean(query.search?.trim())
  const outputColumns = columnIds ? columnIds.length + (documentBased ? 0 : 1) : null
  const oversized = (filtered: boolean) => wordTooLarge(filtered ? rowCount : totalRowCount, outputColumns)
  const wordUnavailable = (filtered: boolean) => outputColumns === null || (filtered ? rowCount : totalRowCount) == null || oversized(filtered)
  const start = (filtered: boolean, selectedFormat: ExportFormat) => {
    if (selectedFormat === ExportFormat.Word && wordUnavailable(filtered)) return
    if (state?.busy || disabled) return
    dispatch(
      ExportActions.start({
        tableId,
        query: {
          format: selectedFormat,
          include_footnotes: documentBased ? includeFootnotes : true,
          filters: filtered ? query.filters : [],
          sorts: query.sorts,
          filter_match: query.filter_match,
          search: filtered ? query.search : undefined,
          search_column_ids: filtered ? query.search_column_ids : undefined,
          search_filenames: filtered ? query.search_filenames : undefined,
          column_ids: columnIds?.filter((id) => id !== 'row'),
          pinned_column_ids: pinnedColumnIds?.filter((id) => id !== 'row'),
          entry_column_index: documentBased && columnIds ? (columnIds.includes('row') ? columnIds.indexOf('row') : null) : 0,
          entry_column_pinned: documentBased ? Boolean(pinnedColumnIds?.includes('row')) : true,
        },
      })
    )
  }
  const button = (
    <IconButton
      size="sm"
      color="black"
      variant="plain"
      icon={<TableDownload />}
      aria-label="Download"
      aria-busy={Boolean(state?.busy)}
      tooltipText="Download"
      disableTooltipInteractive
      loading={state?.busy}
      disabled={disabled}
    />
  )
  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu.Root
        modal={false}
        onOpenChange={() => {
          setFormat(null)
          setWordLimitOpen(false)
        }}
      >
        <DropdownMenu.Trigger asChild>{button}</DropdownMenu.Trigger>
        <MenuContent
          align="end"
          className="w-60"
          onEscapeKeyDown={(event) => {
            if (wordLimitOpen) {
              event.preventDefault()
              setWordLimitOpen(false)
            }
          }}
        >
          <MenuItemGroup>
            {format === null ? (
              <>
                <MenuItem
                  label="Excel (.xlsx)"
                  icon={ExcelIcon}
                  variant="neutral"
                  onSelect={(event) => {
                    if (hasFilters) {
                      event.preventDefault()
                      setFormat(ExportFormat.Excel)
                    } else start(false, ExportFormat.Excel)
                  }}
                />
                {oversized(hasFilters) ? (
                  <UnavailableWordItem open={wordLimitOpen} onOpenChange={setWordLimitOpen} label="Word (.docx)" icon={WordIcon} />
                ) : (
                  <MenuItem
                    label="Word (.docx)"
                    icon={WordIcon}
                    variant="neutral"
                    disabled={wordUnavailable(hasFilters)}
                    onSelect={(event) => {
                      if (hasFilters) {
                        event.preventDefault()
                        setFormat(ExportFormat.Word)
                      } else start(false, ExportFormat.Word)
                    }}
                  />
                )}
              </>
            ) : (
              <>
                <MenuItem
                  label="Back"
                  variant="neutral"
                  onSelect={(event) => {
                    event.preventDefault()
                    setFormat(null)
                  }}
                />
                <MenuItem label="Filtered entries" variant="neutral" onSelect={() => start(true, format)} />
                {format === ExportFormat.Word && oversized(false) ? (
                  <UnavailableWordItem open={wordLimitOpen} onOpenChange={setWordLimitOpen} label="All entries" />
                ) : (
                  <MenuItem
                    label="All entries"
                    variant="neutral"
                    disabled={format === ExportFormat.Word && wordUnavailable(false)}
                    onSelect={() => start(false, format)}
                  />
                )}
              </>
            )}
          </MenuItemGroup>
          {documentBased && (
            <>
              <MenuSeparator />
              <MenuItemGroup>
                <DropdownMenu.CheckboxItem
                  aria-label="Include footnotes"
                  aria-description={FOOTNOTE_HELP}
                  checked={includeFootnotes}
                  onCheckedChange={(checked) => setIncludeFootnotes(checked === true)}
                  onSelect={(event) => event.preventDefault()}
                  className={getMenuItemClassName({ variant: 'neutral' })}
                >
                  <span
                    aria-hidden
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border-strong bg-surface-field group-data-[state=checked]:border-brand-500 group-data-[state=checked]:bg-brand-500"
                  >
                    <DropdownMenu.ItemIndicator>
                      <CheckIcon className="h-3.5 w-3.5 text-white" />
                    </DropdownMenu.ItemIndicator>
                  </span>
                  <MenuItemContent label="Include footnotes" variant="neutral" />
                  <span
                    className="flex shrink-0"
                    onPointerDown={(event) => event.stopPropagation()}
                    onPointerUp={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Tooltip title={FOOTNOTE_HELP}>
                      <span role="img" aria-label="Footnote information" className="flex shrink-0">
                        <InfoCircleFilled aria-hidden className="h-4 w-4 text-text-tertiary" />
                      </span>
                    </Tooltip>
                  </span>
                </DropdownMenu.CheckboxItem>
              </MenuItemGroup>
            </>
          )}
        </MenuContent>
      </DropdownMenu.Root>
      {state?.error && (
        <p role="alert" className="text-sm text-red-700">
          {state.error}
        </p>
      )}
    </div>
  )
}
